import { FC, ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import ScrollArea from '@/storybook/components/ScrollArea/ScrollArea';
import { TestIds } from './testIds';
import styles from './ChatConversation.module.css';

export const SCROLL_BOTTOM_THRESHOLD = 20;

export interface ChatConversationProps {
  children: ReactNode;
}

const ChatConversation: FC<ChatConversationProps> = ({ children }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isUserAtBottom = useRef(true);
  const rafId = useRef(0);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        isUserAtBottom.current =
          container.scrollHeight - container.scrollTop - container.clientHeight < SCROLL_BOTTOM_THRESHOLD;
      });
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId.current);
    };
  }, []);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container || !isUserAtBottom.current) return;

    container.scrollTop = container.scrollHeight;
  }, [children]);

  return (
    <ScrollArea
      scrollbars="y"
      className={styles.conversation}
      viewportRef={scrollRef}
      viewportProps={{
        'role': 'log',
        'aria-live': 'polite',
        'tabIndex': 0,
      }}
      data-testid={TestIds.conversation}
    >
      <div className={styles.messagesWrapper}>{children}</div>
    </ScrollArea>
  );
};

export default ChatConversation;
